/**
 * The phase-suggestion review page — one client, read-only, annotatable.
 *
 * Four tabs, one concern each: the Program (the builder grid), the Compare
 * (the whole arc against the phase it follows, and what the validator made of
 * it), the Inputs it was built from, and the coach's own Notes. Splitting them
 * keeps each screen short enough to work through; a single scroll carrying all
 * four is not a review surface.
 *
 * Validation is not its own tab. The checks exist to be read against the design
 * they judge, so they live on Compare beside the numbers they are about — a
 * coach asked to open a second screen to find out whether the thing they are
 * looking at is sound will read the design and not the verdict.
 *
 * Detail rides in tooltips. The ops AI-coach card already works this way —
 * its SuggestedExerciseRow shows name, sets and reps inline and puts the
 * rationale and basis in a Tooltip — so moving the prose off the page is both
 * shorter and closer to what a coach already knows.
 *
 * Where a student was designed twice, the two arms are a switch rather than
 * two stacked copies: flipping in place puts the differences on the same
 * patch of screen.
 *
 * Nothing here can write to vigeo. There is no apply, no save, no draft.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Me } from "../../auth";
import type {
  Annotation, Arm, Block, Consideration, DroppedVariant, Exercise, LadderExhaustion,
  Phase, Sample, SeedTrace, Variant, Workout,
} from "./types";
import { BasisChip } from "./Basis";
import { CompareTab } from "./Compare";
import { Tip, TipLayer } from "./Tip";
import "./phase-review.css";

/* ============================================================
 *  node addressing — must match infra/worker/src/vigeo/phase_review.ts
 * ============================================================ */

const slug = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);

const nodeIds = {
  sample: (id: string) => `sample:${id}`,
  variant: (sid: string) => `variant:${sid}`,
  phase: (sid: string, p: number) => `variant:${sid}/phase:${p}`,
  workout: (sid: string, p: number, w: string) =>
    `variant:${sid}/phase:${p}/workout:${slug(w)}`,
  block: (sid: string, p: number, w: string, b: number) =>
    `variant:${sid}/phase:${p}/workout:${slug(w)}/block:${b}`,
  exercise: (sid: string, p: number, w: string, b: number, e: string) =>
    `variant:${sid}/phase:${p}/workout:${slug(w)}/block:${b}/exercise:${slug(e)}`,
  consideration: (sampleId: string, i: number) => `consideration:${sampleId}:${i}`,
};

/* ============================================================
 *  annotation store
 * ============================================================ */

interface Mark { verdict: string | null; comment: string }
type SaveState = "idle" | "saving" | "saved" | "error";

function useAnnotations(instance: string, sampleId: string, authorEmail: string) {
  const [marks, setMarks] = useState<Record<string, Mark>>({});
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [others, setOthers] = useState<Annotation[]>([]);
  const [saveState, setSaveState] = useState<Record<string, SaveState>>({});
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    let alive = true;
    fetch(`/api/${instance}/phase-review/annotations?sample=${encodeURIComponent(sampleId)}`)
      .then(r => (r.ok ? r.json() : { annotations: [] }))
      .then((d: { annotations: Annotation[] }) => {
        if (!alive) return;
        const mine: Record<string, Mark> = {};
        const lab: Record<string, string> = {};
        const theirs: Annotation[] = [];
        for (const a of d.annotations || []) {
          if (a.author_email === authorEmail) {
            mine[a.node_path] = { verdict: a.verdict, comment: a.comment || "" };
            if (a.node_label) lab[a.node_path] = a.node_label;
          } else theirs.push(a);
        }
        setMarks(mine); setLabels(lab); setOthers(theirs);
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [instance, sampleId, authorEmail]);

  const push = useCallback((
    nodePath: string, nodeKind: string, nodeLabel: string, next: Mark,
  ) => {
    setSaveState(s => ({ ...s, [nodePath]: "saving" }));
    fetch(`/api/${instance}/phase-review/annotations`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sample: sampleId, nodePath, nodeKind, nodeLabel,
        verdict: next.verdict, comment: next.comment,
      }),
    })
      .then(r => setSaveState(s => ({ ...s, [nodePath]: r.ok ? "saved" : "error" })))
      .catch(() => setSaveState(s => ({ ...s, [nodePath]: "error" })));
  }, [instance, sampleId]);

  const setMark = useCallback((
    nodePath: string, nodeKind: string, nodeLabel: string,
    patch: Partial<Mark>, immediate = false,
  ) => {
    setLabels(l => ({ ...l, [nodePath]: nodeLabel }));
    setMarks(prev => {
      const next: Mark = {
        verdict: patch.verdict !== undefined ? patch.verdict : (prev[nodePath]?.verdict ?? null),
        comment: patch.comment !== undefined ? patch.comment : (prev[nodePath]?.comment ?? ""),
      };
      clearTimeout(timers.current[nodePath]);
      if (immediate) push(nodePath, nodeKind, nodeLabel, next);
      else {
        timers.current[nodePath] = setTimeout(
          () => push(nodePath, nodeKind, nodeLabel, next), 700,
        );
      }
      return { ...prev, [nodePath]: next };
    });
  }, [push]);

  useEffect(() => () => {
    Object.values(timers.current).forEach(clearTimeout);
  }, []);

  /** Remove a mark. An emptied verdict and comment is what the store treats as
   *  deleted, so this is the same write, not a second path. */
  const clearMark = useCallback((nodePath: string, nodeKind: string) => {
    clearTimeout(timers.current[nodePath]);
    const empty: Mark = { verdict: null, comment: "" };
    push(nodePath, nodeKind, labels[nodePath] || "", empty);
    setMarks(prev => {
      const next = { ...prev };
      delete next[nodePath];
      return next;
    });
    setSaveState(st => { const n = { ...st }; delete n[nodePath]; return n; });
  }, [push, labels]);

  const count = Object.values(marks).filter(
    m => m.verdict || (m.comment || "").trim(),
  ).length;

  return { marks, labels, others, saveState, setMark, clearMark, count };
}

type AnnotateApi = ReturnType<typeof useAnnotations>;

/* ============================================================
 *  annotate control
 * ============================================================ */

// The same three words the design-consideration workbook asks for, so a coach
// marking both artefacts is answering one question, not two.
const VERDICTS = [
  { id: "sound", label: "Correct" },
  { id: "unsound", label: "Incorrect" },
  { id: "unsure", label: "Not sure" },
];

/** The button alone. `open` lives in the caller so the editor can be placed
 *  where it fits the layout rather than beside the button. */
function AnnotateButton({ api, nodePath, nodeKind, open, onToggle }: {
  api: AnnotateApi; nodePath: string; nodeKind: string;
  open: boolean; onToggle: () => void;
}) {
  const mark = api.marks[nodePath];
  const has = !!(mark && (mark.verdict || mark.comment.trim()));
  const cls = [
    "pr-annotate-btn",
    has ? "pr-has-mark" : "",
    mark?.verdict === "sound" ? "pr-mark-sound" : "",
    mark?.verdict === "unsound" ? "pr-mark-unsound" : "",
  ].filter(Boolean).join(" ");
  const face = has
    ? (mark.verdict === "sound" ? "✓" : mark.verdict === "unsound" ? "✕" : "•")
    : "+";
  return (
    <Tip text={has ? "Edit your note" : `Add a note on this ${nodeKind}`}>
      <button aria-expanded={open} className={cls} onClick={onToggle} type="button">
        {face}
      </button>
    </Tip>
  );
}

function AnnotateBox({ api, nodePath, nodeKind, nodeLabel, onClose }: {
  api: AnnotateApi; nodePath: string; nodeKind: string; nodeLabel: string;
  onClose?: () => void;
}) {
  const mark = api.marks[nodePath];
  const state = api.saveState[nodePath];
  const fromOthers = api.others.filter(o => o.node_path === nodePath);
  const has = !!(mark && (mark.verdict || (mark.comment || "").trim()));
  const box = useRef<HTMLDivElement>(null);

  // Escape closes it, and so does clicking away — an editor that can only be
  // dismissed by finding its own small button is a trap.
  useEffect(() => {
    if (!onClose) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (box.current && !box.current.contains(t)
          && !(t instanceof Element && t.closest(".pr-annotate-btn"))) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [onClose]);

  return (
    <div className="pr-annotate-box" ref={box}>
      {onClose && (
        <button aria-label="Close" className="pr-annotate-close"
                onClick={onClose} type="button">×</button>
      )}
          <div className="pr-verdicts">
            {VERDICTS.map(v => (
              <button
                aria-pressed={mark?.verdict === v.id}
                className="pr-verdict"
                key={v.id}
                onClick={() => api.setMark(
                  nodePath, nodeKind, nodeLabel,
                  { verdict: mark?.verdict === v.id ? null : v.id }, true,
                )}
                type="button"
              >
                {v.label}
              </button>
            ))}
          </div>
          <textarea
            onChange={e => api.setMark(nodePath, nodeKind, nodeLabel, { comment: e.target.value })}
            placeholder={`Feedback on this ${nodeKind}…`}
            value={mark?.comment || ""}
          />
          <div className="pr-annotate-foot">
            <span className="pr-save-state">
              {state === "saving" && "Saving…"}
              {state === "saved" && "Saved"}
              {state === "error" && "Could not save — check your connection."}
            </span>
            {has && (
              <button
                className="pr-annotate-remove"
                onClick={() => { api.clearMark(nodePath, nodeKind); onClose?.(); }}
                type="button"
              >
                Remove
              </button>
            )}
          </div>
      {fromOthers.map(o => (
        <div className="pr-source" key={o.author_email}>
          <strong>{o.author_email}</strong>
          {o.verdict ? ` — ${o.verdict}` : ""}{o.comment ? `: ${o.comment}` : ""}
        </div>
      ))}
    </div>
  );
}

/** Button + editor together, for the places where an inline box is fine. */
function Annotate({ api, nodePath, nodeKind, nodeLabel }: {
  api: AnnotateApi; nodePath: string; nodeKind: string; nodeLabel: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <span className="pr-annotate-anchor">
      <AnnotateButton
        api={api} nodeKind={nodeKind} nodePath={nodePath}
        onToggle={() => setOpen(o => !o)} open={open}
      />
      {open && (
        <AnnotateBox
          api={api} nodeKind={nodeKind} nodeLabel={nodeLabel} nodePath={nodePath}
          onClose={() => setOpen(false)}
        />
      )}
    </span>
  );
}

/** Where this variant's picks actually came from, counted from the picks.
 *  `provenance` on the variant is the designer's own tally; this is the page
 *  checking it rather than repeating it. */
function countBasis(variant: Variant): Record<string, number> {
  const out: Record<string, number> = {};
  for (const ph of variant.phases) {
    for (const w of ph.workouts) {
      for (const b of w.blocks) {
        for (const e of b.exercises) {
          const k = e.basisResolution?.kind || "creative";
          out[k] = (out[k] || 0) + 1;
        }
      }
    }
  }
  return out;
}

/** What the run could not do. The designer already records these; leaving them
 *  on a tab nobody opens lets a thinner phase read as a considered one. */
const EXHAUSTION_REASON: Record<string, string> = {
  "no-unused-rung": "every rung was already used recently",
  "gated-band-empty": "no rung sits in this student's competency band",
  "none-fits": "no rung fitted the slot",
  "undeclared": "the designer did not say why",
  "declared": "the family ran tight but the ladder still covered it",
};

function droppedText(d: DroppedVariant | string): string {
  if (typeof d === "string") return `variant dropped: ${d}`;
  const why = (d.violations || []).map(v => v.message || v.check).filter(Boolean);
  const tried = (d.repairsAttempted || []).filter(Boolean);
  return [
    `variant dropped — ${d.label}`,
    why.length ? `: ${why.join("; ")}` : "",
    tried.length ? ` — tried first: ${tried.join("; ")}` : "",
  ].join("");
}

function exhaustionText(e: LadderExhaustion): string {
  const why = EXHAUSTION_REASON[e.reason] || e.reason;
  const picked = e.pickedFrom === "library"
    ? `filled from the wider library${e.exercises?.length
        ? ` — ${e.exercises.filter(Boolean).join(", ")}` : ""}`
    : "still filled from the ladder";
  return `${e.family}: ${why}, ${picked}${e.note ? `. ${e.note}` : ""}`;
}

function Roadblocks({ variant, arm }: { variant: Variant; arm: Arm }) {
  const items: { kind: string; text: string }[] = [
    ...(variant.ladderExhaustion || []).map(e => ({
      kind: e.pickedFrom === "library" ? "exhausted" : "warning",
      text: exhaustionText(e),
    })),
    ...variant.warnings.map(w => ({ kind: "warning", text: w })),
    ...variant.sanitizeDrops.map(d => ({ kind: "drop", text: `dropped from the plan: ${d}` })),
    ...arm.droppedVariants.map(d => ({ kind: "drop", text: droppedText(d) })),
    // The run's own warning already states a truncation when it happened; this
    // derived line exists for runs that predate that warning. One fact, once.
    ...(((arm.catalogFallback?.eligible ?? 0) > (arm.catalogFallback?.offered ?? 0))
      && !variant.warnings.some(w => /catalog fallback truncated/i.test(w))
      ? [{
        kind: "exhausted",
        text: `the off-ladder menu was truncated — ${arm.catalogFallback!.offered} of `
          + `${arm.catalogFallback!.eligible} eligible exercises reached the designer, `
          + "so a pick outside the ladder was made from a reduced set",
      }] : []),
  ];
  if (!items.length) return null;
  return (
    <div className="pr-roadblocks">
      <strong className="pr-roadblocks-head">
        {`The designer reported ${items.length} thing${items.length === 1 ? "" : "s"} it could not satisfy`}
      </strong>
      {items.map(i => (
        <div className={`pr-roadblock pr-roadblock-${i.kind}`} key={i.text}>{i.text}</div>
      ))}
    </div>
  );
}

/* ============================================================
 *  Program tab
 * ============================================================ */

/**
 * The variables the builder would show for an exercise.
 *
 * `ExerciseDetail` renders the reps variable first and then up to two others,
 * and it only ever renders a variable whose `expect` is set — so a load the
 * stash never seeded produces no weight cell, in ops and here alike. The unit
 * is the variable's own `unitShort` (`reps`, `kg`, `lbs`).
 */
/**
 * One line describing where a suggested load came from, composed the way the
 * ops design-audit panel composes it (`seedTraceLine`) — every number read
 * from the trace, none derived here.
 */
function seedTraceLine(trace: SeedTrace, unit?: string | null): string {
  if (trace.source === "none") return "no history — unseeded";
  const w = (v: number | string) => `${v}${unit || ""}`;
  const reference = [
    trace.referenceWeight != null && w(trace.referenceWeight),
    trace.referenceReps != null && `× ${trace.referenceReps}`,
    trace.referenceMovementType && `(${trace.referenceMovementType})`,
    trace.e1rm != null && `(e1RM ${w(trace.e1rm)})`,
  ].filter(Boolean).join(" ");
  const extras = [
    trace.weightFactor != null && `factor ${trace.weightFactor}`,
    trace.increment != null && `increment ${w(trace.increment)}`,
  ].filter(Boolean).join(", ");
  let line = `seeded from ${trace.source}`;
  if (reference) line += `: ${reference}`;
  if (trace.targetReps != null) line += ` → ${trace.targetReps} reps`;
  if (extras) line += ` (${extras})`;
  return line;
}

function variablesOf(ex: Exercise): { name: string; expect: string; unitShort: string }[] {
  const out: { name: string; expect: string; unitShort: string }[] = [];
  if (ex.targetReps != null) {
    out.push({ name: "Reps", expect: String(ex.targetReps), unitShort: "reps" });
  }
  if (ex.seededWeight != null) {
    out.push({
      name: "Weight",
      expect: String(ex.seededWeight),
      unitShort: ex.weightUnit || "kg",
    });
  }
  return out;
}

function ExerciseRow({ api, ex, sid, phase, workout, blockIndex, cols, last }: {
  api: AnnotateApi; ex: Exercise; sid: string;
  phase: number; workout: string; blockIndex: number;
  cols: number; last: boolean;
}) {
  const path = nodeIds.exercise(sid, phase, workout, blockIndex, ex.name);
  const vars = variablesOf(ex);
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className={`pr-ex-name ${ex.diff === "only-here" ? "pr-ex-differs" : ""}`}>
        <Tip className="pr-ex-title" text={ex.rationale || "No rationale given."}>
          {ex.name}
        </Tip>
        <span className="pr-exercise-actions">
          <BasisChip basis={ex.basisResolution} />
          <AnnotateButton
            api={api} nodeKind="exercise" nodePath={path}
            onToggle={() => setOpen(o => !o)} open={open}
          />
        </span>
      </div>

      {vars.map(v => (
        v.name === "Weight" && ex.seedTrace ? (
          // Where a load came from is the coach's first question about it.
          <Tip
            className="pr-ex-var"
            key={v.name}
            text={seedTraceLine(ex.seedTrace, v.unitShort)}
          >
            {v.expect}
            {v.unitShort ? ` ${v.unitShort}` : ""}
          </Tip>
        ) : (
          <div className="pr-ex-var" key={v.name}>
            {v.expect}
            {v.unitShort ? ` ${v.unitShort}` : ""}
          </div>
        )
      ))}

      {/* A weight cell exists only where the exercise template carries a weight
          variable — a bodyweight movement has none, and the builder shows
          nothing for it. `seedTrace` is the signal: the stash writes one for
          every exercise it could seed, including `source: none`. */}
      {ex.seededWeight == null && ex.seedTrace && (
        <Tip
          className="pr-ex-var pr-ex-var-none"
          text={seedTraceLine(ex.seedTrace, ex.weightUnit)}
        >
          {`— ${ex.weightUnit || "kg"}`}
        </Tip>
      )}

      {Array.from({ length: Math.max(0, cols - vars.length - 1) }).map((_, i) => (
        <div key={`pad-${i}`} />
      ))}

      {/* The editor floats from the row's end, so opening it cannot push the
          variables out of their columns. */}
      {open && (
        <div className="pr-ex-annotate">
          <AnnotateBox
            api={api} nodeKind="exercise" nodeLabel={ex.name} nodePath={path}
            onClose={() => setOpen(false)}
          />
        </div>
      )}

      {!last && <div className="pr-ex-rule" />}
    </>
  );
}

function BlockCard({ api, block, blockIndex, sid, phase, workout }: {
  api: AnnotateApi; block: Block; blockIndex: number;
  sid: string; phase: number; workout: string;
}) {
  const cols = Math.max(
    2,
    ...block.exercises.map(
      e => variablesOf(e).length + (e.seededWeight == null && e.seedTrace ? 1 : 0),
    ),
  );
  return (
    <div className="pr-block">
      <div className="pr-block-head">
        <span className="pr-block-name">{block.name}</span>
        {block.maxSet != null && <span className="pr-set-count">{`(${block.maxSet} set)`}</span>}
        <span style={{ marginLeft: "auto" }}>
          <Annotate
            api={api}
            nodeKind="block"
            nodeLabel={`${workout} · ${block.name}`}
            nodePath={nodeIds.block(sid, phase, workout, blockIndex)}
          />
        </span>
      </div>
      {/* `extraColumns`: one column per variable on the block's longest
          exercise, never fewer than two. */}
      <div
        className="pr-block-body pr-ex-grid"
        style={{ ["--pr-cols" as string]: String(cols) }}
      >
        {block.exercises.map((ex, i) => (
          <ExerciseRow
            api={api} blockIndex={blockIndex} cols={cols} ex={ex} key={ex.name}
            last={i === block.exercises.length - 1}
            phase={phase} sid={sid} workout={workout}
          />
        ))}
      </div>
    </div>
  );
}

function SessionColumn({ api, workout, index, sid, phase }: {
  api: AnnotateApi; workout: Workout; index: number; sid: string; phase: number;
}) {
  return (
    <div className="pr-session">
      <div className="pr-session-head">
        {/* The builder calls these Sessions; the pipeline names them Workout
            A/B. Both are shown so neither vocabulary trips a reader. */}
        <span className="pr-session-name">{`Session ${index + 1}`}</span>
        <span style={{ alignItems: "center", display: "flex", gap: 8 }}>
          <span className="pr-muted">{workout.name}</span>
          <Annotate
            api={api} nodeKind="workout" nodeLabel={workout.name}
            nodePath={nodeIds.workout(sid, phase, workout.name)}
          />
        </span>
      </div>
      <div className="pr-session-body">
        {workout.blocks.map((b, bi) => (
          <BlockCard
            api={api} block={b} blockIndex={bi + 1} key={b.name}
            phase={phase} sid={sid} workout={workout.name}
          />
        ))}
      </div>
    </div>
  );
}

function PhaseCard({ api, phase, sid }: { api: AnnotateApi; phase: Phase; sid: string }) {
  const title = phase.stimulusFocus
    ? `Phase ${phase.position} — ${phase.stimulusFocus}`
    : `Phase ${phase.position}`;
  return (
    <div className="pr-phase">
      <div className="pr-phase-head">
        <span className="pr-phase-name">{title}</span>
        <Annotate
          api={api} nodeKind="phase" nodeLabel={title}
          nodePath={nodeIds.phase(sid, phase.position)}
        />
      </div>
      {phase.phaseRationale && (
        <p className="pr-phase-why">{phase.phaseRationale}</p>
      )}
      <div className="pr-sessions">
        {phase.workouts.map((w, wi) => (
          <SessionColumn
            api={api} index={wi} key={w.name}
            phase={phase.position} sid={sid} workout={w}
          />
        ))}
      </div>
    </div>
  );
}

/* ============================================================
 *  Inputs tab
 * ============================================================ */

function ConsiderationCard({ api, c, sampleId }: {
  api: AnnotateApi; c: Consideration; sampleId: string;
}) {
  return (
    <div className="pr-consideration">
      <div className="pr-cons-head">
        <span className="pr-tag">{c.type}</span>
        {c.severity && (
          <span className={`pr-tag ${c.severity === "avoid" ? "pr-tag-avoid" : ""}`}>
            {c.severity}
          </span>
        )}
        {c.scope && <span className="pr-tag">{c.scope}</span>}
        {c.avoidPatterns.length > 0 && (
          <Tip
            text={`Withheld from the design entirely: ${c.avoidPatterns.join(", ")}`}
          >
            <span className="pr-tag pr-tag-avoid">withholds {c.avoidPatterns.length}</span>
          </Tip>
        )}
        {c.privacyFlag && (
          <Tip text={c.privacyFlag}>
            <span className="pr-tag pr-tag-privacy">privacy</span>
          </Tip>
        )}
        {(c.extrapolated || c.sources.length === 0) && (
          <Tip
            text={c.sources.length === 0
              ? "No coach note is cited. The system inferred this on its own."
              : "Reads beyond what the cited notes say."}
          >
            <span className="pr-tag pr-tag-inferred">Inferred</span>
          </Tip>
        )}
        {c.expiresAt && (
          <Tip text="No coach note states this timeline; the date is the system's own estimate.">
            <span className="pr-tag">expires {c.expiresAt}</span>
          </Tip>
        )}
        {!c.expiresAt && c.expiryNote && (
          <Tip text={`The note's own words on duration: ${c.expiryNote}`}>
            <span className="pr-tag">expires</span>
          </Tip>
        )}
        <span style={{ marginLeft: "auto" }}>
          <Annotate
            api={api} nodeKind="consideration" nodeLabel={c.note.slice(0, 80)}
            nodePath={nodeIds.consideration(sampleId, c.index)}
          />
        </span>
      </div>

      <div className="pr-cons-note">{c.note}</div>

      {c.sources.length > 0 && (
        <details className="pr-disclosure">
          <summary>{`${c.sources.length} source note${c.sources.length === 1 ? "" : "s"}`}</summary>
          {c.sources.map(s => <div className="pr-source" key={s}>{s}</div>)}
        </details>
      )}
    </div>
  );
}

/** Where a competency gate came from, in a coach's words. */
const GATE_SOURCE: Record<string, { label: string; note: string }> = {
  "coach": {
    label: "Set by the coach",
    note: "A competency feed was supplied for this run; it overrides everything else.",
  },
  "assessment": {
    label: "From the onboarding assessment",
    note: "Read from the student's recorded assessment, one level per movement type.",
  },
  "history": {
    label: "From training history",
    note: "Read from how deep the student has recently trained each pattern — the "
      + "rung they reached, as a fraction of that family's own ladder.",
  },
  "history+assessment": {
    label: "From history and assessment",
    note: "Each family takes the most current source that can answer for it: recent "
      + "training where there is any, the onboarding assessment where there is not.",
  },
  "default-medium": {
    label: "Manual default",
    note: "Nothing is known about this student's competency, so every family gates at "
      + "medium — regressions and base rungs, never a progression. The manual's default "
      + "when competency is not defined.",
  },
};

/** Which source answered for one family, as a chip beside its band. */
const FAMILY_SOURCE: Record<string, { label: string; cls: string; note: string }> = {
  coach: {
    label: "Coach", cls: "pr-src-coach",
    note: "Supplied for this run; it overrides everything else.",
  },
  history: {
    label: "History", cls: "pr-src-history",
    note: "Read from what this student has recently trained.",
  },
  assessment: {
    label: "Assessment", cls: "pr-src-assessment",
    note: "From the onboarding assessment — training history has not covered this "
      + "pattern.",
  },
  default: {
    label: "Default", cls: "pr-src-default",
    note: "Neither training history nor an assessment answers for this pattern, so "
      + "the manual default applies.",
  },
};

const BAND_TONE: Record<string, string> = {
  low: "pr-band-low", medium: "pr-band-medium", high: "pr-band-high",
};

/** The gate decides which rung every pick may come from, so it steers the
 *  design as much as a consideration does. It is shown beside them. */
function CompetencyPanel({ arm }: { arm: Arm }) {
  const c = arm.competency;
  if (!c || !c.gate || Object.keys(c.gate).length === 0) return null;
  const face = GATE_SOURCE[c.source] || { label: c.source || "Unstated", note: "" };
  const families = Object.entries(c.gate).sort(([a], [b]) => a.localeCompare(b));
  const counts = families.reduce<Record<string, number>>((acc, [, band]) => {
    acc[band] = (acc[band] || 0) + 1;
    return acc;
  }, {});
  const uniform = Object.keys(counts).length === 1;

  return (
    <div className="pr-panel">
      <div className="pr-h2" style={{ alignItems: "center", display: "flex", gap: 8 }}>
        Movement competency
        <Tip text={face.note}>
          <span className={`pr-tag ${c.source === "default-medium" ? "pr-tag-inferred" : ""}`}>
            {face.label}
          </span>
        </Tip>
      </div>
      <p className="pr-muted" style={{ marginBottom: 10, maxWidth: "70ch" }}>
        {uniform
          ? `Every family is gated at ${families[0][1]}, so no pick was chosen for this `
            + "student in particular."
          : "Each family gates which ladder rung a pick may come from — low takes the "
            + "regression, medium the base, high the progression."}
      </p>
      <div className="pr-gate">
        {families.map(([fam, band]) => {
          const src = FAMILY_SOURCE[(c.sources || {})[fam]] || null;
          const ev = (c.evidence || {})[fam];
          return (
            <div className={`pr-gate-row ${BAND_TONE[band] || ""}`} key={fam}>
              <div className="pr-gate-head">
                <span className="pr-gate-fam">{fam.replace(/-/g, " ")}</span>
                <span className="pr-gate-flags">
                  {ev?.stalled && (
                    <Tip
                      className="pr-tip-inline"
                      text={"This pattern sat on the same rung in every phase read. "
                        + "A plateau and a rung never offered look identical from the "
                        + "record, so it is raised rather than answered."}
                    >
                      <span className="pr-gate-stalled">Stalled</span>
                    </Tip>
                  )}
                  <span className="pr-gate-band">{band}</span>
                </span>
              </div>
              {(src || ev) && (
                <div className="pr-gate-evidence">
                  {src && (
                    <Tip className="pr-tip-inline" text={src.note}>
                      <span className={`pr-src ${src.cls}`}>{src.label}</span>
                    </Tip>
                  )}
                  {ev && (
                    <Tip
                      className="pr-tip-flex"
                      text={`Rung ${ev.rungIndex + 1} of ${ev.rungCount} on this `
                        + "family's ladder — the deepest the student trained inside the "
                        + "window."}
                    >
                      <span className="pr-gate-rung">
                        {ev.rung?.label || `Rung ${ev.rungIndex + 1}`}
                        {ev.rung?.exercise ? ` · ${ev.rung.exercise}` : ""}
                      </span>
                    </Tip>
                  )}
                  {ev && (
                    <span className="pr-gate-vol">
                      {`${ev.sets} sets · ${ev.phases} `}
                      {ev.phases === 1 ? "phase" : "phases"}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InputsTab({ api, sample, arm }: { api: AnnotateApi; sample: Sample; arm: Arm }) {
  const notes = sample.inputs.notes;
  const noteCount = notes.groups.reduce((n, g) => n + g.notes.length, 0);

  const facts = [
    { label: "Age", value: sample.student.age ?? "—" },
    { label: "Sessions / wk", value: sample.student.sessionsPerWeek ?? "—" },
    { label: "Notes on file", value: noteCount },
    {
      label: "Feedback",
      value: notes.feedback.ratings,
      // The count is ratings. Calling it feedback implies written comments,
      // and across this sample not one carries any.
      tip: `${notes.feedback.ratings} ratings, ${notes.feedback.written} with written text.`,
    },
    {
      label: "Past-phase ex.",
      value: sample.inputs.excludedCount,
      tip: "Exercises the designer held back from reuse.",
    },
  ];

  return (
    <>
      <div className="pr-panel">
        <div className="pr-facts">
          {facts.map(f => (
            <div className="pr-fact" key={f.label}>
              <div className="pr-fact-label">{f.label}</div>
              {f.tip ? (
                <Tip className="pr-fact-value" text={f.tip}>{f.value}</Tip>
              ) : (
                <div className="pr-fact-value">{f.value}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="pr-panel">
        <div className="pr-h2" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {`Design considerations (${sample.inputs.considerations.length})`}
          {sample.inputs.considerations.length > 0 && (() => {
            const src = sample.arms[0]?.considerationsFed?.source;
            if (!src) return null;
            return (
              <Tip
                text={src.stored > 0
                  ? `${src.stored} from the approved store, ${src.input} derived for this run.`
                  : "Derived for this run from the student's notes — not yet in the approved store."}
              >
                <span className="pr-tag">
                  {src.stored > 0 ? `${src.stored} stored · ${src.input} derived` : "Derived this run"}
                </span>
              </Tip>
            );
          })()}
        </div>
        {sample.inputs.considerations.length === 0 ? (
          <div className="pr-empty">
            None exist for this student — no injury or onboarding notes to derive
            any from. The suggestion came from past phases and feedback alone.
          </div>
        ) : (
          sample.inputs.considerations.map(c => (
            <ConsiderationCard api={api} c={c} key={c.index} sampleId={sample.id} />
          ))
        )}
      </div>

      <CompetencyPanel arm={arm} />

      {notes.groups.length > 0 && (
        <div className="pr-panel">
          <div className="pr-h2">{`Notes on file (${noteCount})`}</div>
          {notes.groups.map(g => (
            <details className="pr-disclosure" key={g.group}>
              <summary>{`${g.group} · ${g.notes.length}`}</summary>
              {g.notes.map((n, i) => (
                <div className="pr-source" key={`${g.group}-${i}`}>
                  {n.title ? <strong>{`${n.title} — `}</strong> : null}
                  {n.text}
                  {n.date ? <span className="pr-note-date">{` (${n.date})`}</span> : null}
                </div>
              ))}
            </details>
          ))}
        </div>
      )}

      {sample.inputs.pastPhases.length > 0 && (
        <div className="pr-panel">
          <div className="pr-h2">
            {`Previous phases (${sample.inputs.pastPhases.length})`}
          </div>
          <div className="pr-muted" style={{ marginBottom: 8 }}>
            These exercises were held back from reuse.
          </div>
          {sample.inputs.pastPhases.map(ph => (
            <details className="pr-disclosure" key={ph.label}>
              <summary>{`${ph.label} · ${ph.exercises.length} exercises`}</summary>
              <div className="pr-past-ex">
                {ph.exercises.map((e, i) => (
                  <span className="pr-past-chip" key={`${ph.label}-${i}`}>{e}</span>
                ))}
              </div>
            </details>
          ))}
        </div>
      )}
    </>
  );
}

/* ============================================================
 *  Notes tab
 * ============================================================ */

function NotesTab({ api, sample }: { api: AnnotateApi; sample: Sample }) {
  const rows = Object.entries(api.marks)
    .filter(([, m]) => m.verdict || (m.comment || "").trim());

  if (!rows.length) {
    return (
      <div className="pr-panel pr-empty">
        No notes yet. Use the + on any phase, session, block, exercise or
        consideration; everything you write lands here.
      </div>
    );
  }

  return (
    <div className="pr-panel">
      {rows.map(([path, m]) => (
        <div className="pr-note-row" key={path}>
          <div className="pr-note-where">
            {api.labels[path] || path.split("/").pop()}
          </div>
          <div>
            {m.verdict && (
              <span className={`pr-note-verdict pr-v-${m.verdict}`}>{m.verdict}</span>
            )}
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>{m.comment}</div>
          </div>
          <button
            className="pr-note-remove"
            onClick={() => api.clearMark(path, path.split(":")[0] || "sample")}
            type="button"
          >
            Remove
          </button>
        </div>
      ))}
      <div className="pr-muted" style={{ marginTop: 10 }}>
        {`${rows.length} note${rows.length === 1 ? "" : "s"} on ${sample.student.name}. Saved as you type.`}
      </div>
    </div>
  );
}

/* ============================================================
 *  page
 * ============================================================ */

type TabId = "program" | "compare" | "inputs" | "notes";

export function PhaseReview({ instance, sampleId, me }: {
  instance: string; sampleId: string; me: Me;
}) {
  const [sample, setSample] = useState<Sample | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // ?tab= deep-links a concern, so a specific screen can be pointed at directly.
  // `checks` was its own tab and is now part of Compare; an existing link lands
  // on the content it was pointing at.
  const [tab, setTab] = useState<TabId>(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    if (t === "checks") return "compare";
    return (["program", "compare", "inputs", "notes"] as const)
      .includes(t as TabId) ? (t as TabId) : "program";
  });
  const [armId, setArmId] = useState<string>("");
  const [variantIx, setVariantIx] = useState(0);
  const api = useAnnotations(instance, sampleId, me.email || "");

  useEffect(() => {
    let alive = true;
    fetch(`/api/${instance}/phase-review/s/${encodeURIComponent(sampleId)}`)
      .then(async r => {
        if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
        return r.json();
      })
      .then((d: Sample) => {
        if (!alive) return;
        setSample(d);
        const want = new URLSearchParams(window.location.search).get("arm");
        setArmId(d.arms.find(a => a.id === want)?.id || d.arms[0]?.id || "");
      })
      .catch(e => alive && setErr(String(e.message || e)));
    return () => { alive = false; };
  }, [instance, sampleId]);

  const arm: Arm | undefined = useMemo(
    () => sample?.arms.find(a => a.id === armId) || sample?.arms[0],
    [sample, armId],
  );
  const variant = arm?.variants[Math.min(variantIx, (arm?.variants.length || 1) - 1)];

  /** Exercises in this variant that the other arm does not contain. */
  const diffCount = useMemo(() => {
    if (!variant) return 0;
    let n = 0;
    for (const ph of variant.phases) {
      for (const w of ph.workouts) {
        for (const b of w.blocks) {
          for (const e of b.exercises) if (e.diff === "only-here") n += 1;
        }
      }
    }
    return n;
  }, [variant]);

  if (err) return <div className="pr"><p>Could not load this page. {err}</p></div>;
  if (!sample || !arm || !variant) return <div className="pr"><p className="pr-muted">Loading…</p></div>;

  const TABS: { id: TabId; label: string; count?: number }[] = [
    { id: "program", label: "Program" },
    { id: "compare", label: "Compare" },
    { id: "inputs", label: "Inputs", count: sample.inputs.considerations.length },
    { id: "notes", label: "My notes", count: api.count },
  ];

  return (
    <div className="pr">
      <TipLayer />

      <div className="pr-head">
        <h1>{sample.student.name}</h1>
      </div>

      <div className="pr-tabs" role="tablist">
        {TABS.map(t => (
          <button
            aria-selected={tab === t.id}
            className="pr-tab"
            key={t.id}
            onClick={() => setTab(t.id)}
            role="tab"
            type="button"
          >
            {t.label}
            {t.count ? <span className="pr-tab-count">{t.count}</span> : null}
          </button>
        ))}
      </div>

      {(tab === "program" || tab === "compare") && (
        <div className="pr-toolbar">
          {sample.arms.length > 1 && (
            <span className="pr-switch-group">
              <span className="pr-switch-label">Design considerations</span>
              <span className="pr-switch">
                {sample.arms.map(a => (
                  <button
                    aria-pressed={a.id === arm.id}
                    key={a.id}
                    onClick={() => { setArmId(a.id); setVariantIx(0); }}
                    type="button"
                  >
                    {a.id === "with" ? "With" : "Without"}
                  </button>
                ))}
              </span>
            </span>
          )}

          {arm.variants.length > 1 && (
            <span className="pr-switch">
              {arm.variants.map((v, i) => (
                <Tip
                  key={v.suggestionId}
                  text={v.rationale || "No rationale given for this variant."}
                >
                  <button
                    aria-pressed={i === variantIx}
                    onClick={() => setVariantIx(i)}
                    type="button"
                  >
                    {v.variantLabel}
                  </button>
                </Tip>
              ))}
            </span>
          )}

          <span className="pr-muted">{`${variant.exerciseCount} exercises`}</span>

          {(() => {
            const c = countBasis(variant);
            const unverified = (c["manual-unverified"] || 0) + (c["creative"] || 0);
            const cited = (c["ladder"] || 0) + (c["manual"] || 0)
              + (c["manual-verified"] || 0);
            return (
              <Tip
                className={`pr-prov ${unverified ? "pr-prov-flagged" : ""}`}
                sub={unverified
                  ? "An unverified pick cites something the checker cannot find in the manual."
                  : undefined}
                text={Object.entries(c)
                  .map(([k, n]) => `${n} ${k}`).join(" · ")}
              >
                {unverified
                  ? `${unverified} of ${cited + unverified} unverified`
                  : "every pick cited and resolved"}
              </Tip>
            );
          })()}

          {diffCount > 0 && (
            <Tip
              className="pr-muted pr-diff-key"
              text={diffCount === 1
                ? "This exercise appears in this version only, marked with a blue edge."
                : `These ${diffCount} exercises appear in this version only, marked with a blue edge.`}
            >
              {`${diffCount} only here`}
            </Tip>
          )}

          {arm.considerationsFed.avoidFamilies.length > 0 && (
            <Tip
              className="pr-tag pr-tag-avoid"
              text={`Withheld from the design entirely: ${arm.considerationsFed.avoidFamilies.join(", ")}`}
            >
              {`${arm.considerationsFed.avoidFamilies.length} families withheld`}
            </Tip>
          )}

          <span style={{ marginLeft: "auto" }}>
            <Annotate
              api={api} nodeKind="variant" nodeLabel={variant.variantLabel}
              nodePath={nodeIds.variant(variant.suggestionId)}
            />
          </span>
        </div>
      )}

      {(tab === "program" || tab === "compare") && (
        <Roadblocks arm={arm} variant={variant} />
      )}

      {tab === "program" && variant.phases.map(ph => (
        <PhaseCard api={api} key={ph.position} phase={ph} sid={variant.suggestionId} />
      ))}

      {tab === "compare" && (
        <CompareTab
          canWrite={Boolean(me.is_superadmin
            || (me.memberships?.[instance]
              && me.memberships[instance] !== "member"))}
          instance={instance}
          pastPhases={sample.inputs.pastPhases}
          student={sample.student.name}
          variant={variant}
        />
      )}
      {tab === "inputs" && <InputsTab api={api} arm={arm} sample={sample} />}
      {tab === "notes" && <NotesTab api={api} sample={sample} />}
    </div>
  );
}
